import React, { memo, useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { validators } from 'investira.sdk';
import {
  Stack,
  LoadingButton,
  Icon,
  Divider,
  Tooltip,
  IconButton,
  Typography,
  CircularProgress
} from 'investira.react.components';
import { PageTemplate } from '../../../components/templates';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import services from '../../../services';
import AvaliacoesOutputHighlights from './AvaliacoesOutputHighlights';
import AvaliacoesOutputResumo from './AvaliacoesOutputResumo';
import AvaliacoesOutputPerfomance from './AvaliacoesOutputPerfomance';
import AvaliacaoOutputHeader from './AvaliacaoOutputHeader';
import AvaliacoesOutputRespostas from './AvaliacoesOutputRespostas/AvaliacoesOutputRespostas';

const computeNota = rData => {
  if (!rData) return 0;

  const scoreGeral = rData.score_geral ?? rData.nota;
  if (scoreGeral !== null && scoreGeral !== undefined) {
    return Number(scoreGeral);
  }

  const ql = rData.nota_qualitativa ?? 0;
  const qt = rData.nota_quantitativa ?? 0;
  return Number(ql) * 0.7 + Number(qt) * 0.3;
};

const resolveGestor = (rData, contextGestor) => {
  if (rData && rData.gestor && Object.keys(rData.gestor).length > 0) {
    return rData.gestor;
  }
  return contextGestor;
};

const AvaliacoesOutput = memo(props => {
  const { state } = useAvaliacoesContext();
  const { gestor: contextGestor } = state;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [rData, setRData] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchResultado = async () => {
      setLoading(true);
      setFetchError(null);

      try {
        const rRes = await services.mockAvaliacoes.resultadosRepo.getByAvaliacaoId(id);

        if (cancelled) return;

        if (!rRes.ok) {
          setFetchError(rRes.error?.message || 'Erro ao carregar resultado');
          setLoading(false);
          return;
        }

        if (!rRes.data || rRes.data.length === 0) {
          setRData(null);
          setLoading(false);
          return;
        }

        setRData(rRes.data[0]);
      } catch (err) {
        if (!cancelled) {
          setFetchError(err?.message || 'Erro inesperado ao carregar resultado');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (id) {
      fetchResultado();
    } else {
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [id]);

  const NOTA_QUALITATIVA = rData?.nota_qualitativa ?? Math.round(Math.random() * 3);
  const NOTA_QUANTITATIVA = rData?.nota_quantitativa ?? Math.round(Math.random() * 3);
  const NOTA = rData ? computeNota(rData) : NOTA_QUALITATIVA * 0.7 + NOTA_QUANTITATIVA * 0.3;
  const gestor = resolveGestor(rData, contextGestor);

  const title = rData?.gestor?.pessoa_fantasia || gestor?.pessoa_fantasia || 'Alpha Asset Management Ltda.';

  if (loading) {
    return (
      <PageTemplate
        title={'Carregando...'}
        subtitle={'Avaliação Gestor'}
        maxWidth={'xl'}>
        <Stack alignItems="center" justifyContent="center" py={8}>
          <CircularProgress />
          <Typography variant="body2" color="textSecondary" mt={2}>
            Carregando resultado da avaliação...
          </Typography>
        </Stack>
      </PageTemplate>
    );
  }

  if (fetchError) {
    return (
      <PageTemplate
        title={title}
        subtitle={'Avaliação Gestor'}
        maxWidth={'xl'}>
        <Stack alignItems="center" justifyContent="center" py={8}>
          <Typography variant="h6" color="error">
            {fetchError}
          </Typography>
        </Stack>
      </PageTemplate>
    );
  }

  if (!rData) {
    return (
      <PageTemplate
        title={title}
        subtitle={'Avaliação Gestor'}
        maxWidth={'xl'}>
        <Stack alignItems="center" justifyContent="center" py={8}>
          <Typography variant="h6" color="textSecondary">
            Nenhum resultado disponível para esta avaliação
          </Typography>
        </Stack>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title={title}
      subtitle={'Avaliação Gestor'}
      maxWidth={'xl'}
      headerRenderActions={
        <Stack direction="row" spacing={2} alignItems="center">
          <LoadingButton
            //disabled={!props.isFinished || !validators.isEmpty(props.filesErrors)}
            //loading={!props.isFinished}
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="download" size={16} />}
            size="small"
            sx={{ px: 2 }}>
            PDF
          </LoadingButton>
          <Tooltip title={`Docs`}>
            <IconButton color="primary" size="small" onClick={() => navigate(`/avaliacoes/docs`)}>
              <Icon iconName="question" size={24} />
            </IconButton>
          </Tooltip>
        </Stack>
      }>
      <Stack gap={3}>
        <Stack gap={1}>
          <AvaliacaoOutputHeader
            created={rData.created || rData.updated_at}
            gestor={gestor}
            usuario={rData.usuario}
          />
          <Divider />
        </Stack>
        <Stack gap={2} flexGrow={1} direction="row">
          <AvaliacoesOutputHighlights
            nota={NOTA}
            notaQualitativa={NOTA_QUALITATIVA}
            notaQuantitativa={NOTA_QUANTITATIVA}
            proximaAvaliacao={rData.proxima_avaliacao}
            justificativas={rData.justificativas}
          />
          <AvaliacoesOutputPerfomance sessoes={rData.sessoes} />
        </Stack>
        <Divider />
        {/* TODO: Exibir dados da gestora com logo */}
        <AvaliacoesOutputResumo gestor={gestor} nota={NOTA} resumo={rData.resumo} />
        <Divider />
        <AvaliacoesOutputRespostas data={rData.respostas} />
      </Stack>
    </PageTemplate>
  );
});

AvaliacoesOutput.displayName = 'AvaliacoesOutput';

export default AvaliacoesOutput;
